import React, {useState, useEffect, useCallback, useRef} from "react"
import {Box, CircularProgress, Skeleton, Typography} from "@mui/material"

import {SMSDraftReplyPanel} from "./SMSDraftReplyPanel"
import type {ReplyAction} from "./SMSMessageRow"
import {SMSThread} from "./SMSThread"

import {api} from "@indebted/api"
import type {ConversationMessage, ConversationTimeline} from "@indebted/api"
import {useDraftSMSReply} from "@indebted/api/conversationDraftSMSReplyAPI"
import {useSendSMSReply} from "@indebted/api/conversationSendSMSReplyAPI"
import {useNotification} from "@indebted/components/Notification"
import {useAgent} from "@indebted/hooks/Agent"
import {colors} from "@indebted/theme/colors"

const PAGE_SIZE = 5
const REPLY_SYNC_ATTEMPTS = 8
const REPLY_SYNC_INTERVAL_MS = 700

function delay(ms: number): Promise<void> {
	return new Promise((resolve) => setTimeout(resolve, ms))
}

function SMSConversationView({
	customerID,
	conversationID,
	preferredCaseID,
	onPickedUp,
}: {
	customerID: string
	conversationID: string
	preferredCaseID?: string
	onPickedUp?: () => void
}) {
	const {notification} = useNotification()
	const {mutateAsync: draftReply} = useDraftSMSReply()
	const {mutateAsync: sendReply} = useSendSMSReply()
	const [agentEmail, agentName] = useAgent()
	const [timeline, setTimeline] = useState<ConversationTimeline | null>(null)
	const [messages, setMessages] = useState<ConversationMessage[]>([])
	const [hasMore, setHasMore] = useState(false)
	const [loading, setLoading] = useState(true)
	const [loadingMore, setLoadingMore] = useState(false)
	const [selectedReplyMessageId, setSelectedReplyMessageId] = useState<string | null>(null)
	const [pickingUpCase, setPickingUpCase] = useState(false)
	const [syncingReply, setSyncingReply] = useState(false)
	const [syncTimedOut, setSyncTimedOut] = useState(false)

	const mounted = useRef(true)
	const loadedCountRef = useRef(0)
	useEffect(() => {
		return () => {
			mounted.current = false
		}
	}, [])

	const fetchTimeline = useCallback(async () => {
		// Refetch covers every message already loaded, so it keeps the "Show earlier"
		// history instead of collapsing back to the first page.
		const conversation = await api.customer.getConversation(customerID, conversationID, {
			limit: Math.max(PAGE_SIZE, loadedCountRef.current),
			offset: 0,
			agentEmail,
		})
		if (!mounted.current) return null
		setTimeline(conversation)
		setMessages(conversation.messages)
		setHasMore(conversation.hasMore)
		loadedCountRef.current = conversation.messages.length
		return conversation
	}, [customerID, conversationID, agentEmail])

	// The reply is recorded before it is projected, so the timeline is briefly
	// missing what the agent just sent. Poll the refetch until the row lands
	// rather than settling on a thread that looks like nothing happened.
	const waitForSentReply = useCallback(
		async (repliedToProviderMessageID: string) => {
			setSyncingReply(true)
			setSyncTimedOut(false)
			try {
				for (let attempt = 0; attempt < REPLY_SYNC_ATTEMPTS; attempt++) {
					try {
						const conversation = await fetchTimeline()
						if (!conversation) return
						// The agent reply is projected under the inbound message's own
						// provider message ID, with no in-reply-to of its own, so
						// direction is what separates the two rows.
						const landed = conversation.messages.some(
							(m) => m.direction === "agent-reply" && m.providerMessageId === repliedToProviderMessageID,
						)
						if (landed) return
					} catch {
						// A transient read failure should not abort the sync; keep polling.
					}
					if (attempt < REPLY_SYNC_ATTEMPTS - 1) await delay(REPLY_SYNC_INTERVAL_MS)
				}
				if (mounted.current) setSyncTimedOut(true)
			} finally {
				if (mounted.current) setSyncingReply(false)
			}
		},
		[fetchTimeline],
	)

	const loadConversation = useCallback(async () => {
		try {
			await fetchTimeline()
		} catch {
			if (mounted.current) notification.error("Failed to load the conversation")
		}
	}, [fetchTimeline, notification])

	useEffect(() => {
		if (!agentEmail) return
		setLoading(true)
		loadConversation().finally(() => {
			if (mounted.current) setLoading(false)
		})
	}, [loadConversation, agentEmail])

	async function pickUpLinkedCase(caseID: string, email: string, name: string) {
		setPickingUpCase(true)
		try {
			await api.customer.pickUpCase(customerID, caseID, {AgentEmail: email, AgentName: name})
			notification.success("Case picked up")
		} catch (error: any) {
			notification.error(error?.Message ?? "Failed to pick up case")
		}
		// Two agents can race for the same case, so both outcomes settle against the server.
		await loadConversation()
		onPickedUp?.()
		if (mounted.current) setPickingUpCase(false)
	}

	function showEarlier() {
		setLoadingMore(true)
		api.customer
			.getConversation(customerID, conversationID, {limit: PAGE_SIZE, offset: messages.length})
			.then((conversation) => {
				if (!mounted.current) return
				setMessages((current) => [...current, ...conversation.messages])
				setHasMore(conversation.hasMore)
				loadedCountRef.current += conversation.messages.length
			})
			.catch(() => {
				if (mounted.current) notification.error("Failed to load earlier messages")
			})
			.finally(() => {
				if (mounted.current) setLoadingMore(false)
			})
	}

	if (loading) {
		return <SMSThreadSkeleton />
	}

	if (!timeline) {
		return null
	}

	const replyTarget = selectedReplyMessageId ?? timeline.replyAnchorProviderMessageId

	// A backend that predates case ownership answers without these fields, and a
	// conversation that cannot be read is worse than one that cannot be gated.
	const linkedCases = timeline.linkedCases ?? []
	const ownershipGated = timeline.caseOwnershipEnabled && linkedCases.length > 0
	const ownsLinkedCase = linkedCases.some((linked) => !!linked.assigneeEmail && linked.assigneeEmail === agentEmail)
	const unassignedLinkedCase =
		linkedCases.find((linked) => linked.caseId === preferredCaseID && !linked.assigneeEmail) ??
		linkedCases.find((linked) => !linked.assigneeEmail)
	const canDraftReply = !ownershipGated || ownsLinkedCase

	const replyActionFor = (message: ConversationMessage): ReplyAction | undefined => {
		if (message.direction !== "inbound") return undefined
		if (canDraftReply) {
			return {kind: "draft", onDraftReply: () => setSelectedReplyMessageId(message.providerMessageId)}
		}
		if (!unassignedLinkedCase) return {kind: "held-by-another-agent"}
		if (!agentEmail || !agentName) return undefined
		return {
			kind: "pick-up",
			pickingUp: pickingUpCase,
			onPickUp: () => void pickUpLinkedCase(unassignedLinkedCase.caseId, agentEmail, agentName),
		}
	}

	return (
		<Box
			sx={{borderTop: `1px solid ${colors.gray["200"]}`, pt: 2.5}}
			display="flex"
			gap={3}
			alignItems="flex-start"
		>
			<Box flex={1} minWidth={0} display="flex" flexDirection="column" gap={1.5}>
				{syncingReply && (
					<Box display="flex" alignItems="center" gap={1}>
						<CircularProgress size={14} />
						<Typography variant="caption" color="textSecondary">
							Updating timeline…
						</Typography>
					</Box>
				)}
				{syncTimedOut && !syncingReply && (
					<Typography variant="caption" color="textSecondary">
						Reply sent. It is taking longer than usual to appear — refresh to confirm.
					</Typography>
				)}
				<SMSThread
					messages={messages}
					replyAnchorProviderMessageId={timeline.replyAnchorProviderMessageId}
					replyTargetProviderMessageId={replyTarget}
					hasMore={hasMore}
					loadingMore={loadingMore}
					onShowEarlier={showEarlier}
					replyActionFor={replyActionFor}
				/>
			</Box>
			{replyTarget && canDraftReply && (
				<Box flex={1} minWidth={0}>
					{/* Keyed on the target so picking a different message starts a fresh
					    draft rather than carrying the last one's instructions over. */}
					<SMSDraftReplyPanel
						key={replyTarget}
						fromNumber={timeline.toAddress}
						toNumber={timeline.fromAddress}
						authoredBy={agentEmail ?? ""}
						onGenerateDraft={(request) =>
							draftReply({
								customerID,
								conversationID,
								providerMessageId: replyTarget,
								...request,
							})
						}
						onSendReply={async ({text, guidance, modelVersion}) => {
							await sendReply({
								customerID,
								conversationID,
								providerMessageId: replyTarget,
								text,
								guidance,
								modelVersion,
								authoredBy: agentEmail ?? "",
							})
							// Not awaited: the panel confirms the send as soon as the route
							// accepts it, while the thread catches up beside it.
							void waitForSentReply(replyTarget)
						}}
					/>
				</Box>
			)}
		</Box>
	)
}

function SMSThreadSkeleton() {
	return (
		<Box display="flex" flexDirection="column" gap={2.5}>
			{[0, 1].map((i) => (
				<Box key={i} p={4} border="1px solid" borderColor="grey.200" borderRadius={1}>
					<Box display="flex" justifyContent="space-between" mb={1.5}>
						<Skeleton variant="text" width={160} />
						<Skeleton variant="rounded" width={72} height={20} />
					</Box>
					<Skeleton variant="text" width="90%" />
				</Box>
			))}
		</Box>
	)
}

export {SMSConversationView}
